/**
 * Сессия человека. Один источник правды на всё приложение.
 *
 * Сессия живёт в httpOnly-cookie: из JS её не прочитать, поэтому «залогинен ли я» —
 * это ответ сервера на GET /api/v1/auth/session, а не флаг в памяти. Отсюда три состояния,
 * а не два: пока ответ не пришёл, рисовать нельзя ни приложение, ни форму входа — иначе
 * при каждом обновлении страницы человек видит мигание логина.
 */

import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { get, onUnauthorized, post } from './api/client.ts';

export interface SessionUser {
  readonly id: string;
  readonly login: string;
  readonly display_name: string;
  readonly role: string;
}

interface SessionResponse {
  readonly user: SessionUser;
  readonly expires_at: string;
}

export interface SessionState {
  readonly user: SessionUser | null;
  readonly expiresAt: string | null;
  readonly ready: boolean;
  readonly refresh: () => void;
  readonly logout: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const load = useCallback((): (() => void) => {
    let alive = true;
    get<SessionResponse>('/api/v1/auth/session')
      .then((s) => {
        if (!alive) return;
        setUser(s.user);
        setExpiresAt(s.expires_at);
      })
      .catch(() => {
        if (!alive) return;
        setUser(null);
        setExpiresAt(null);
      })
      .finally(() => {
        if (alive) setReady(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => load(), [load]);

  const refresh = useCallback(() => {
    load();
  }, [load]);

  // 401 на любом запросе приложения означает, что сессия кончилась прямо сейчас.
  useEffect(
    () =>
      onUnauthorized(() => {
        setUser(null);
        setExpiresAt(null);
      }),
    [],
  );

  const logout = useCallback(async () => {
    await post<void>('/api/v1/auth/logout');
    setUser(null);
    setExpiresAt(null);
  }, []);

  return (
    <SessionContext.Provider value={{ user, expiresAt, ready, refresh, logout }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const s = useContext(SessionContext);
  if (s === null) throw new Error('useSession вне SessionProvider');
  return s;
}
